import { FileUploadOutlined } from "@mui/icons-material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeselectIcon from "@mui/icons-material/Deselect";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllEntityTypeList } from "src/clients/EntityService";
import { getAllTestDataFeatureDTOByClientIdAndUserDetailsId } from "src/clients/TestDataFeatureService";
import {
  deleteTestDataFiles,
  downLoadTestDataFiles,
  saveTestDataFile,
  uploadTestDataFiles,
} from "src/clients/TestDataService";
import { getUserFeatureFiles } from "src/redux/slices/userFilesSlice";
import { selectUser } from "src/redux/slices/userSlice";
import { Feature } from "src/types/Feature";
import { getOrgs } from "src/types/Organizations";
import { TestDataFeatureDto } from "src/types/TestDataFeatureDto";
import { LOCATION, TOAST_MESSAGE } from "../constants/Constants";
import { useTestDataContext } from "../context/Context";
import WarningPopup from "../modal/WarningPopup";
import NotifyError from "../notifications/NotifyError";
import NotifySuccess from "../notifications/NotifySuccess";
import NotifyWarning from "../notifications/NotifyWarning";
import EditTestData from "./edit/EditTestData";
import testDataTabSubCompnentsStyles from "./TestDataTabSubComponent.module.css";
import ViewTestData from "./view/ViewTestData";

interface testDataTabSubComponentProps {
  isDefault: Boolean;
  mode: string | null;
  fetchTestData: () => Promise<void>;
  handleOnSetMode: (mode: string | null) => void;
  handleOnSwitchTab: (tabValue: string) => void;
}

const TestDataTabSubComponent = ({
  isDefault,
  mode,
  fetchTestData,
  handleOnSetMode,
  handleOnSwitchTab,
}: testDataTabSubComponentProps) => {
  const [selectedOrgCode, setSelectedOrgCode] = useState<string>("");
  const { initialTestData } = useTestDataContext();
  const [selectedTestDataFiles, setSelectedTestDataFiles] = useState<string[]>(
    []
  );
  const [isView, setIsView] = useState<boolean>(false);
  const [selectedTestData, setSelectedTestData] = useState<any>();
  const [entityTypeList, setEntityTypeList] = useState<string[]>([]);
  const [testDataFeatureMap, setTestDataFeatureMap] = useState<
    Map<string, TestDataFeatureDto>
  >(new Map());
  const [currentOrgTestDataFeatureMap, setCurrentOrgTestDataFeatureMap] =
    useState<Map<string, TestDataFeatureDto>>(new Map());
  const [isViewToEdit, setIsViewToEdit] = useState<boolean>(false);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [isUploadPopup, setIsUploadPopup] = useState<boolean>(false);
  const [isDisable, isLoading] = useState<boolean>(false);
  const [isCloneEnable, setIsCloneEnable] = useState<boolean>(false);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isNewTestDataFile, setIsNewTestDataFile] = useState<boolean>(false);
  const [newTestDataFileName, setNewTestDataFileName] = useState<string>("");
  const [entityType, setEntityType] = useState<string>("");
  const [uploadedTestDataFilesList, setUploadedTestDataFilesList] = useState<
    File[]
  >([]);
  const [uploadPopupSelectedOrg, setUploadPopupSelectedOrg] =
    useState<string>("");
  const [mappedFeatureList, setMappedFeatureList] = useState<string[]>([]);
  const userfiles = useSelector(getUserFeatureFiles) || [];
  const userFeatureNameList = userfiles.userFiles
    ?.filter((feature: Feature) => feature.location === LOCATION.IN_PROGRESS)
    .map((feature: Feature) => feature.name.replaceAll(" ", "_"));
  const user = useSelector(selectUser);
  const clonedDataString = localStorage.getItem("clonedData");
  const clonedData = clonedDataString && JSON.parse(clonedDataString);

  const allFeatureFiles = Array.from(testDataFeatureMap.values())
    .filter((dto: TestDataFeatureDto) => {
      const organizationToCompare = uploadPopupSelectedOrg || selectedOrgCode;
      return (
        dto.organization === organizationToCompare &&
        dto.entityTypeName === entityType
      );
    })
    .flatMap((dto) => dto.featureFilesList);

  const filteredUserFeatureNameList = userFeatureNameList?.filter(
    (featureName: string) =>
      mappedFeatureList.includes(featureName) ||
      !allFeatureFiles.includes(featureName)
  );

  useEffect(() => {
    const orgCodes = Array.from(
      initialTestData.get(isDefault ? "Default" : user.userId)?.keys() || []
    );
    if (orgCodes.length > 0) {
      setSelectedOrgCode(orgCodes[0]);
    }
  }, [initialTestData, isDefault, user.userId]);

  useEffect(() => {
    if (clonedData) {
      setIsView(true);
      setEntityType(clonedData.testDataFeatureDTO.entityTypeName);
      setMappedFeatureList(clonedData.testDataFeatureDTO.featureFilesList);
      setNewTestDataFileName(clonedData.testDataFeatureDTO.testDataFileName);
      setSelectedOrgCode(clonedData.testDataFeatureDTO.organization);
      setSelectedTestDataFiles([
        clonedData.testDataFeatureDTO.testDataFileName + ".json",
      ]);
      setSelectedTestData(clonedData.testDataClonedData);
      handleOnSetMode("view");
      localStorage.clear();
    }
  }, [clonedData, handleOnSetMode]);

  useEffect(() => {
    getEntityTypeList();
    getAllTestDataFeature();
  }, [user.productCode, user.selectedClient]);

  useEffect(() => {
    const filteredMap = Array.from(testDataFeatureMap.entries()).filter(
      ([key, value]) => {
        return value.organization === selectedOrgCode;
      }
    );

    // Set the filtered map to state
    setCurrentOrgTestDataFeatureMap(new Map(filteredMap));
  }, [selectedOrgCode, testDataFeatureMap, isDefault]);

  const getEntityTypeList = async () => {
    const entityTypeListRes = await getAllEntityTypeList(
      user.auth,
      user.productCode
    );
    if (entityTypeListRes.ok) {
      setEntityTypeList(await entityTypeListRes.json());
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
  };

  const getAllTestDataFeature = async () => {
    const testDataFeatureRes =
      await getAllTestDataFeatureDTOByClientIdAndUserDetailsId(
        user.auth,
        user.clientId,
        user.userDetailsId
      );
    if (testDataFeatureRes.ok) {
      const testDataFeatureObject = await testDataFeatureRes.json();
      setTestDataFeatureMap(new Map(Object.entries(testDataFeatureObject)));
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
  };

  const handleOrgCodeSelect = (event: any) => {
    setSelectedTestDataFiles([]);
    let { value } = event.target;
    setSelectedOrgCode(value);
  };

  const getAllCurrentScreenTestDataNames = () => {
    return Array.from(
      initialTestData
        ?.get(isDefault ? "Default" : user.userId)
        ?.get(selectedOrgCode) || new Map<string, any>()
    ).map(([key]) => key);
  };

  const getAllUserTestDataNames = () => {
    return Array.from(
      initialTestData
        ?.get(user.userId)
        ?.get(uploadPopupSelectedOrg || selectedOrgCode) ||
        new Map<string, any>()
    ).map(([key]) => key);
  };

  const isFileNameUnique = !getAllUserTestDataNames().some(
    (key) => key.replace(".json", "") === newTestDataFileName
  );

  const handleSelectAll = () => {
    setSelectedTestDataFiles(getAllCurrentScreenTestDataNames());
  };

  const handleDeSelectAll = () => {
    setSelectedTestDataFiles([]);
  };

  const handleOnEntityTypeChange = (selectedEntityTypeValue: string) => {
    if (entityType === selectedEntityTypeValue) {
      setEntityType("");
    } else {
      setEntityType(selectedEntityTypeValue);
    }
  };

  const handleOnCheckBoxClick = (folderName: string) => {
    if (selectedTestDataFiles.includes(folderName)) {
      const updatedSelectedTestDataFiles = selectedTestDataFiles.filter(
        (existingFolderName) => existingFolderName !== folderName
      );
      setSelectedTestDataFiles(updatedSelectedTestDataFiles);
    } else {
      setSelectedTestDataFiles([...selectedTestDataFiles, folderName]);
    }
  };

  const handleDeletePopupOk = async () => {
    setDeletePopup(false);
    isLoading(true);
    const testDataFeatureDTO: TestDataFeatureDto = {
      clientId: user.clientId,
      entityTypeName: "",
      featureFilesList: [],
      organization: selectedOrgCode,
      testDataFileName: "",
      previousTestDataFileName: "",
      userDetailsId: user.userDetailsId,
    };
    const response = await deleteTestDataFiles(
      selectedTestDataFiles,
      testDataFeatureDTO,
      selectedOrgCode,
      user.auth,
      user.productCode,
      user.selectedClient
    );
    if (response.ok) {
      let message = "";
      selectedTestDataFiles.map(
        (file) => (message = message + file.replace(".json", "") + ", ")
      );
      await fetchTestData();
      toast.success(
        <NotifySuccess header={"Success"}>
          <Typography variant="body2">
            {message.substring(0, message.length - 2)} deleted successfully
          </Typography>
        </NotifySuccess>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else if (response.status === 403) {
      response.text().then((text) => {
        const message =
          JSON.parse(text).message || "Failed To Delete Test Data File(s)";
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">{message}</Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      });
    } else if (response.status === 408) {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
    isLoading(false);
    setSelectedTestDataFiles([]);
  };

  const handleDeletePopupCancel = () => {
    setDeletePopup(false);
  };

  const handleFileUpload = async (e: any) => {
    setUploadedTestDataFilesList(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const handleUploadPopupCancel = () => {
    setIsUploadPopup(false);
    setUploadedTestDataFilesList([]);
    setUploadPopupSelectedOrg("");
    setEntityType("");
    setMappedFeatureList([]);
  };

  const handleUploadPopupOk = async () => {
    const uploadedDuplicateFilesList = getUploadedDuplicateFiles();
    if (uploadedDuplicateFilesList.length === 0) {
      setIsUploadPopup(false);
      isLoading(true);
      const testDataFeatureDTO: TestDataFeatureDto = {
        clientId: user.clientId,
        entityTypeName: entityType,
        featureFilesList: mappedFeatureList,
        organization: uploadPopupSelectedOrg,
        testDataFileName: "",
        previousTestDataFileName: "",
        userDetailsId: user.userDetailsId,
      };
      const response = await uploadTestDataFiles(
        uploadedTestDataFilesList,
        testDataFeatureDTO,
        uploadPopupSelectedOrg,
        user.auth,
        user.productCode,
        user.selectedClient
      );
      if (response.ok) {
        await handleOnSaveBack();
        toast.success(
          <NotifySuccess header={"Success"}>
            <Typography variant="body2">
              Test Data File(s) uploaded successfully
            </Typography>
          </NotifySuccess>,
          {
            icon: false,
            closeButton: false,
          }
        );
      } else if (response.status === 408) {
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {TOAST_MESSAGE.FAILED_TO_CONNECT}
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      } else {
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">
              {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
            </Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      }
      setUploadedTestDataFilesList([]);
      setUploadPopupSelectedOrg("");
      isLoading(false);
    } else {
      toast.warning(
        <NotifyWarning header={"Warning"}>
          <Typography variant="body2">File(s) already exist :</Typography>
          <Typography variant="body2">
            <span style={{ color: "#FE524A" }}>
              {uploadedDuplicateFilesList.join(", ")}
            </span>{" "}
          </Typography>
        </NotifyWarning>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
  };

  const getUploadedDuplicateFiles = () => {
    const TestDataNameList = Array.from(
      initialTestData
        ?.get(isDefault ? "Default" : user.userId)
        ?.get(uploadPopupSelectedOrg) || new Map<string, any>()
    ).map(([key]) => key);

    return uploadedTestDataFilesList
      .filter((file) => TestDataNameList.includes(file.name))
      .map((file) => file.name);
  };

  const handleCreatePopupCancel = () => {
    setIsCreate(false);
    setNewTestDataFileName("");
    setEntityType("");
    setUploadPopupSelectedOrg("");
    setMappedFeatureList([]);
  };

  const handleCreatePopupContinue = () => {
    setIsCreate(false);
    setIsNewTestDataFile(true);
    handleCreate();
  };

  const handleOnDownload = async () => {
    isLoading(true);
    const response = await downLoadTestDataFiles(
      user.auth,
      selectedTestDataFiles,
      isDefault,
      selectedOrgCode,
      user.productCode,
      user.selectedClient
    );
    if (response.ok) {
      const byteArray = await response.arrayBuffer();
      const zipBlob = new Blob([new Uint8Array(byteArray)], {
        type: "application/zip",
      });
      const blobUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "TestData.zip";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } else if (response.status === 408) {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
    isLoading(false);
    setSelectedTestDataFiles([]);
  };

  const handleClonePopupCancel = () => {
    setIsCloneEnable(false);
    setNewTestDataFileName("");
    setEntityType("");
    setUploadPopupSelectedOrg("");
    setMappedFeatureList([]);
  };

  const handleOnClone = () => {
    setIsCloneEnable(true);
    let baseFileName = selectedTestDataFiles[0].replace(".json", "");
    setNewTestDataFileName(baseFileName);
    const testDataFeature = currentOrgTestDataFeatureMap?.get(
      baseFileName + ":" + selectedOrgCode
    );
    setEntityType(testDataFeature?.entityTypeName ?? "");
  };

  const handleOnCloneToView = async (
    testDataFeatureDTO: TestDataFeatureDto,
    testDataClonedData: any
  ) => {
    const combinedData = { testDataFeatureDTO, testDataClonedData };
    localStorage.setItem("clonedData", JSON.stringify(combinedData));
    handleOnSwitchTab("2");
  };

  const handleOnCloneSave = async () => {
    setIsCloneEnable(false);
    isLoading(true);
    const testDataFeatureDTO: TestDataFeatureDto = {
      clientId: user.clientId,
      entityTypeName: entityType,
      featureFilesList: mappedFeatureList,
      organization: uploadPopupSelectedOrg,
      testDataFileName: newTestDataFileName,
      previousTestDataFileName: "",
      userDetailsId: user.userDetailsId,
    };
    const testDataClonedData = initialTestData
      ?.get(isDefault ? "Default" : user.userId)
      ?.get(selectedOrgCode)
      ?.get(selectedTestDataFiles[0]);
    const response = await saveTestDataFile(
      testDataClonedData,
      testDataFeatureDTO,
      user.auth,
      newTestDataFileName,
      uploadPopupSelectedOrg,
      user.productCode,
      user.selectedClient
    );
    if (response.ok) {
      await handleOnSaveBack();
      toast.success(
        <NotifySuccess header={"Success"}>
          <Typography variant="body2">
            {newTestDataFileName} file saved successfully
          </Typography>
        </NotifySuccess>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else if (response.status === 408) {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.FAILED_TO_CONNECT}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    } else {
      toast.error(
        <NotifyError header={"Error"}>
          <Typography variant="body2">
            {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
          </Typography>
        </NotifyError>,
        {
          icon: false,
          closeButton: false,
        }
      );
    }
    isLoading(false);
    handleOnCloneToView(testDataFeatureDTO, testDataClonedData);
  };

  const handleView = (viewFile?: string) => {
    viewFile && setIsView(true);
    const testDataFeature = currentOrgTestDataFeatureMap?.get(
      viewFile
        ? viewFile.replace(".json", "") + ":" + selectedOrgCode
        : selectedTestDataFiles[0].replace(".json", "") + ":" + selectedOrgCode
    );
    setEntityType(testDataFeature?.entityTypeName ?? "");
    setMappedFeatureList(testDataFeature?.featureFilesList ?? []);
    setNewTestDataFileName(
      viewFile
        ? viewFile.replace(".json", "")
        : selectedTestDataFiles[0].replace(".json", "")
    );
    setSelectedTestData(
      initialTestData
        ?.get(isDefault ? "Default" : user.userId)
        ?.get(selectedOrgCode)
        ?.get(viewFile ? viewFile : selectedTestDataFiles[0])
    );
    handleOnSetMode("view");
  };

  const handleOnViewBack = () => {
    if (isView) setSelectedTestDataFiles([]);
    setIsView(false);
    handleOnSetMode(null);
    setNewTestDataFileName("");
    setEntityType("");
    setSelectedTestData(null);
    setMappedFeatureList([]);
  };

  const handleOnEdit = (isViewToEdit?: boolean) => {
    const testDataFeature = currentOrgTestDataFeatureMap?.get(
      selectedTestDataFiles[0]?.replace(".json", "") + ":" + selectedOrgCode
    );
    setEntityType(
      testDataFeature ? testDataFeature.entityTypeName : entityType
    );
    setMappedFeatureList(
      testDataFeature ? testDataFeature.featureFilesList : mappedFeatureList
    );
    setNewTestDataFileName(
      testDataFeature
        ? selectedTestDataFiles[0].replace(".json", "")
        : newTestDataFileName
    );
    setSelectedTestData(
      testDataFeature
        ? initialTestData
            ?.get(isDefault ? "Default" : user.userId)
            ?.get(selectedOrgCode)
            ?.get(selectedTestDataFiles[0])
        : selectedTestData
    );
    handleOnSetMode("edit");
    setIsViewToEdit(isViewToEdit || false);
  };

  const handleOnEditSaveBack = () => {
    handleOnSetMode(null);
    setIsNewTestDataFile(false);
    setNewTestDataFileName("");
    setEntityType("");
    setUploadPopupSelectedOrg("");
    setSelectedTestData(null);
    setMappedFeatureList([]);
  };

  const handleOnEditBack = () => {
    if (!isViewToEdit) {
      handleOnEditSaveBack();
    } else {
      handleView();
    }
    setIsViewToEdit(false);
  };

  const handleOnSaveBack = async () => {
    isLoading(true);
    await fetchTestData();
    getAllTestDataFeature();
    setSelectedTestDataFiles([]);
    handleOnEditSaveBack();
    isLoading(false);
  };

  const handleCreate = () => {
    handleOnSetMode("edit");
    setSelectedTestData({});
  };

  const handleOnFeatureFileMappedList = (featureName: string) => {
    if (mappedFeatureList.includes(featureName)) {
      setMappedFeatureList((prev) =>
        prev.filter((item) => item !== featureName)
      );
    } else {
      setMappedFeatureList((prev) => [...prev, featureName]);
    }
  };

  if (initialTestData.size === 0) {
    return (
      <Box className="loaderIconStylings">
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return (
    <>
      {isDisable && (
        <Box className={testDataTabSubCompnentsStyles.overlay}></Box>
      )}
      {isDisable && (
        <Box className={testDataTabSubCompnentsStyles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      )}
      {!mode && (
        <>
          {initialTestData.get(isDefault ? "Default" : user.userId)?.size ??
          0 ? (
            <>
              <Box className={testDataTabSubCompnentsStyles.headerBox}>
                <Box sx={{ width: "80%" }}>
                  <FormControl sx={{ width: "18%", margin: "1rem" }}>
                    <InputLabel>Org Code</InputLabel>
                    <Select
                      size="small"
                      value={selectedOrgCode}
                      label="orgCodeList"
                      name="orgCodeList"
                      onChange={handleOrgCodeSelect}
                    >
                      {Array.from(
                        initialTestData
                          .get(isDefault ? "Default" : user.userId)
                          ?.keys() || []
                      ).map((orgCode: string) => (
                        <MenuItem
                          key={orgCode}
                          value={orgCode}
                        >
                          <ListItemText primary={orgCode} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    visibility:
                      selectedTestDataFiles.length > 0 ? "visible" : "hidden",
                  }}
                >
                  {getAllCurrentScreenTestDataNames().every((key) =>
                    selectedTestDataFiles.includes(key)
                  ) ? (
                    <Tooltip
                      title="Deselectall"
                      placement="bottom"
                    >
                      <IconButton
                        size="large"
                        disableRipple={true}
                        onClick={handleDeSelectAll}
                      >
                        <DeselectIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="Selectall"
                      placement="bottom"
                    >
                      <IconButton
                        size="large"
                        disableRipple={true}
                        onClick={handleSelectAll}
                      >
                        <SelectAllIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Box className={testDataTabSubCompnentsStyles.OutterBox}>
                {Array.from(
                  initialTestData
                    ?.get(isDefault ? "Default" : user.userId)
                    ?.get(selectedOrgCode) || new Map<string, any>()
                ).map(([key, value], index) => (
                  <Box
                    className={testDataTabSubCompnentsStyles.Box}
                    key={index}
                  >
                    <Checkbox
                      className="checkcss"
                      checked={selectedTestDataFiles.includes(key)}
                      onChange={() => handleOnCheckBoxClick(key)}
                    />
                    <Tooltip
                      placement="top-start"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [5, -10],
                              },
                            },
                          ],
                        },
                      }}
                      title={key.replace(".json", "")}
                    >
                      <Link
                        sx={{
                          color: "#fe524a",
                          textDecoration: "none",
                          fontWeight: "bold",
                          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                          fontSize: "14px",
                          overflow: "hidden",
                          textWrap: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                        onClick={() => {
                          setSelectedTestDataFiles([key]);
                          handleView(key);
                        }}
                      >
                        {key.replace(".json", "")}
                      </Link>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography
              className={testDataTabSubCompnentsStyles.NoFilesMessage}
              variant="subtitle1"
            >
              No Test Data Found
            </Typography>
          )}
          {isCloneEnable && (
            <WarningPopup
              isOpen={true}
              onClose={handleClonePopupCancel}
              actionButtons={[
                {
                  name: "OK",
                  isDisabled:
                    newTestDataFileName === "" ||
                    !isFileNameUnique ||
                    entityType === "",
                  onButtonClick: handleOnCloneSave,
                },
                {
                  name: "Cancel",
                  onButtonClick: handleClonePopupCancel,
                },
              ]}
            >
              <Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    File Name
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <TextField
                    placeholder="Enter Test Data File Name"
                    size="small"
                    sx={{
                      width: "15rem",
                    }}
                    helperText={
                      isFileNameUnique ? "" : "Test Data File already exists"
                    }
                    type={"text"}
                    value={newTestDataFileName}
                    onChange={(e) => setNewTestDataFileName(e.target.value)}
                  />
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Entity Type
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={entityType}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}> Entity Type</span>
                        )
                      }
                    >
                      {entityTypeList.map((entityType) => (
                        <MenuItem
                          key={entityType}
                          value={entityType}
                          onClick={() => handleOnEntityTypeChange(entityType)}
                        >
                          {entityType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Org Code
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                    fullWidth
                  >
                    <Select
                      value={uploadPopupSelectedOrg}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}>Select Org Code</span>
                        )
                      }
                    >
                      {getOrgs(user.productCode).map((orgCode) => (
                        <MenuItem
                          key={orgCode}
                          value={orgCode}
                          onClick={() => setUploadPopupSelectedOrg(orgCode)}
                        >
                          {orgCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>Mapped Feature Files :</Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={mappedFeatureList}
                      size="small"
                      displayEmpty
                      multiple
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(selectedValues) => (
                        <Box className={testDataTabSubCompnentsStyles.chipBox}>
                          {selectedValues.length !== 0 ? (
                            selectedValues.map((value: string) => (
                              <Chip
                                key={value}
                                label={value}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDelete={() => {
                                  handleOnFeatureFileMappedList(value);
                                }}
                              />
                            ))
                          ) : (
                            <span style={{ color: "#aaa" }}>
                              Mapped Feature Files
                            </span>
                          )}
                        </Box>
                      )}
                    >
                      {filteredUserFeatureNameList?.length > 0 ? (
                        filteredUserFeatureNameList.map(
                          (featureName: string) => (
                            <MenuItem
                              key={featureName}
                              value={featureName}
                              onClick={() =>
                                handleOnFeatureFileMappedList(featureName)
                              }
                            >
                              {featureName}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem value={"No User Files Found"}>
                          {"No User Files Found"}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </WarningPopup>
          )}
          {deletePopup && (
            <WarningPopup
              isOpen={true}
              onClose={handleDeletePopupCancel}
              actionButtons={[
                {
                  name: "Confirm",
                  onButtonClick: handleDeletePopupOk,
                },
                {
                  name: "Cancel",
                  onButtonClick: handleDeletePopupCancel,
                },
              ]}
            >
              <Typography>
                Do you want to delete the test data file(s)
              </Typography>
            </WarningPopup>
          )}
          {isCreate && (
            <WarningPopup
              isOpen={true}
              onClose={handleCreatePopupCancel}
              actionButtons={[
                {
                  name: "Continue",
                  isDisabled:
                    newTestDataFileName === "" ||
                    !isFileNameUnique ||
                    entityType === "" ||
                    uploadPopupSelectedOrg === "",
                  onButtonClick: handleCreatePopupContinue,
                },
                {
                  name: "Cancel",
                  onButtonClick: handleCreatePopupCancel,
                },
              ]}
            >
              <Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    File Name
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <TextField
                    placeholder="Enter Test Data File Name"
                    size="small"
                    sx={{
                      width: "15rem",
                    }}
                    helperText={
                      isFileNameUnique ? "" : "Test Data File already exists"
                    }
                    type={"text"}
                    value={newTestDataFileName}
                    onChange={(e) => setNewTestDataFileName(e.target.value)}
                  />
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Entity Type
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={entityType}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}> Entity Type</span>
                        )
                      }
                    >
                      {entityTypeList.map((entityType) => (
                        <MenuItem
                          key={entityType}
                          value={entityType}
                          onClick={() => handleOnEntityTypeChange(entityType)}
                        >
                          {entityType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Org Code
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                    fullWidth
                  >
                    <Select
                      value={uploadPopupSelectedOrg}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}>Select Org Code</span>
                        )
                      }
                    >
                      {getOrgs(user.productCode).map((orgCode) => (
                        <MenuItem
                          key={orgCode}
                          value={orgCode}
                          onClick={() => setUploadPopupSelectedOrg(orgCode)}
                        >
                          {orgCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>Mapped Feature Files :</Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={mappedFeatureList}
                      size="small"
                      displayEmpty
                      multiple
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(selectedValues) => (
                        <Box className={testDataTabSubCompnentsStyles.chipBox}>
                          {selectedValues.length !== 0 ? (
                            selectedValues.map((value: string) => (
                              <Chip
                                key={value}
                                label={value}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDelete={() => {
                                  handleOnFeatureFileMappedList(value);
                                }}
                              />
                            ))
                          ) : (
                            <span style={{ color: "#aaa" }}>
                              Mapped Feature Files
                            </span>
                          )}
                        </Box>
                      )}
                    >
                      {filteredUserFeatureNameList?.length > 0 ? (
                        filteredUserFeatureNameList.map(
                          (featureName: string) => (
                            <MenuItem
                              key={featureName}
                              value={featureName}
                              onClick={() =>
                                handleOnFeatureFileMappedList(featureName)
                              }
                            >
                              {featureName}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem value={"No User Files Found"}>
                          {"No User Files Found"}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </WarningPopup>
          )}
          {isUploadPopup && (
            <WarningPopup
              isOpen={true}
              onClose={handleUploadPopupCancel}
              actionButtons={[
                {
                  name: "OK",
                  isDisabled:
                    uploadedTestDataFilesList.length === 0 ||
                    entityType === "" ||
                    uploadPopupSelectedOrg === "",
                  onButtonClick: handleUploadPopupOk,
                },
                {
                  name: "Cancel",
                  onButtonClick: handleUploadPopupCancel,
                },
              ]}
            >
              <Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Upload Files
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<FileUploadOutlined />}
                    component="label"
                  >
                    <input
                      id="file-upload-button"
                      accept=".json"
                      type="file"
                      title={
                        uploadedTestDataFilesList.length > 0
                          ? uploadedTestDataFilesList
                              .map((file) => file.name)
                              .join("\n")
                          : "No File Chosen"
                      }
                      onChange={handleFileUpload}
                      multiple
                      style={{
                        display: "block",
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        opacity: 0,
                        cursor: "pointer",
                      }}
                    />
                    Upload Test Data file(s)
                  </Button>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Entity Type
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={entityType}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}> Entity Type</span>
                        )
                      }
                    >
                      {entityTypeList.map((entityType) => (
                        <MenuItem
                          key={entityType}
                          value={entityType}
                          onClick={() => handleOnEntityTypeChange(entityType)}
                        >
                          {entityType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>
                    Org Code
                    <span
                      style={{
                        color: "#f66",
                      }}
                    >
                      *
                    </span>
                    :
                  </Typography>
                  <FormControl
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                    fullWidth
                  >
                    <Select
                      value={uploadPopupSelectedOrg}
                      size="small"
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value !== "" ? (
                          value
                        ) : (
                          <span style={{ color: "#aaa" }}>Select Org Code</span>
                        )
                      }
                    >
                      {getOrgs(user.productCode).map((orgCode) => (
                        <MenuItem
                          key={orgCode}
                          value={orgCode}
                          onClick={() => setUploadPopupSelectedOrg(orgCode)}
                        >
                          {orgCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
                  <Typography>Mapped Feature Files :</Typography>
                  <FormControl
                    fullWidth
                    className={testDataTabSubCompnentsStyles.dropdownBox}
                  >
                    <Select
                      value={mappedFeatureList}
                      size="small"
                      displayEmpty
                      multiple
                      disabled={uploadedTestDataFilesList.length > 1}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 25 * 10,
                          },
                        },
                      }}
                      renderValue={(selectedValues) => (
                        <Box className={testDataTabSubCompnentsStyles.chipBox}>
                          {selectedValues.length !== 0 ? (
                            selectedValues.map((value: string) => (
                              <Chip
                                key={value}
                                label={value}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDelete={() => {
                                  handleOnFeatureFileMappedList(value);
                                }}
                              />
                            ))
                          ) : (
                            <span style={{ color: "#aaa" }}>
                              Mapped Feature Files
                            </span>
                          )}
                        </Box>
                      )}
                    >
                      {filteredUserFeatureNameList.length > 0 ? (
                        filteredUserFeatureNameList.map(
                          (featureName: string) => (
                            <MenuItem
                              key={featureName}
                              value={featureName}
                              onClick={() =>
                                handleOnFeatureFileMappedList(featureName)
                              }
                            >
                              {featureName}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem value={"No User Files Found"}>
                          {"No User Files Found"}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </WarningPopup>
          )}
          <Box className={testDataTabSubCompnentsStyles.actionBox}>
            <Box className={testDataTabSubCompnentsStyles.createPopupBox}>
              {selectedTestDataFiles.length > 0 && (
                <Typography
                  sx={{
                    color: "#191F1C",
                  }}
                >
                  Selected : ({selectedTestDataFiles.length})
                </Typography>
              )}
            </Box>

            {/* action buttons */}
            <Box className={testDataTabSubCompnentsStyles.actionButtonsBox}>
              {!isDefault && (
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  disabled={selectedTestDataFiles.length === 0}
                  onClick={() => {
                    setDeletePopup(true);
                  }}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={selectedTestDataFiles.length === 0}
                onClick={handleOnDownload}
              >
                Download
              </Button>
              {!isDefault && (
                <Button
                  variant="outlined"
                  startIcon={<FileUploadOutlined />}
                  disabled={selectedTestDataFiles.length !== 0 || !!isDefault}
                  onClick={() => {
                    setIsUploadPopup(true);
                    setUploadPopupSelectedOrg(selectedOrgCode);
                  }}
                >
                  Upload file(s)
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<FileCopyOutlinedIcon />}
                disabled={selectedTestDataFiles.length !== 1}
                onClick={() => {
                  handleOnClone();
                  setUploadPopupSelectedOrg(selectedOrgCode);
                }}
              >
                Clone
              </Button>
              <Button
                variant="outlined"
                startIcon={<RemoveRedEyeOutlinedIcon />}
                disabled={selectedTestDataFiles.length !== 1}
                onClick={() => handleView()}
              >
                View
              </Button>
              {!isDefault && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    disabled={selectedTestDataFiles.length !== 1}
                    onClick={() => handleOnEdit()}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<NoteAddOutlinedIcon />}
                    disabled={selectedTestDataFiles.length !== 0 || !!isDefault}
                    onClick={() => {
                      setIsCreate(true);
                      setUploadPopupSelectedOrg(selectedOrgCode);
                    }}
                  >
                    Create Test File
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </>
      )}
      {mode === "view" && (
        <ViewTestData
          isDefault={isDefault}
          initialTestData={selectedTestData}
          handleOnViewBack={handleOnViewBack}
          handleOnEdit={handleOnEdit}
          entityType={entityType}
          mappedFeatureList={mappedFeatureList}
          testDataFileName={newTestDataFileName}
        />
      )}
      {mode === "edit" && (
        <EditTestData
          isNewTestDataFile={isNewTestDataFile}
          testDataFileName={newTestDataFileName}
          entityType={entityType}
          mappedFeatureList={mappedFeatureList}
          selectedOrgCode={uploadPopupSelectedOrg || selectedOrgCode}
          entityTypeList={entityTypeList}
          userFeatureNameList={filteredUserFeatureNameList}
          selectedTestData={selectedTestData}
          handleOnEditBack={handleOnEditBack}
          handleOnSaveBack={handleOnSaveBack}
        />
      )}
    </>
  );
};

export default TestDataTabSubComponent;
